import type {ReactNode} from 'react'
import {useCallback, useEffect, useId, useRef, useState} from 'react'
import {XIcon} from '@primer/octicons-react'
import {IconButton, useFocusTrap, useOnEscapePress} from '@primer/react'
import {useSlots} from '@primer/react/experimental'
import {clsx} from 'clsx'
import {ssrSafeWindow} from '@github-ui/ssr-utils'
import styles from './panel.module.css'
import {useViewportFitHeight} from './use-viewport-fit-height'
import {useDialogFillHeight} from './use-dialog-fill-height'
import {
  useCopilotSidePanelActions,
  useCopilotSidePanelFocusRefs,
  useIsCopilotSidePanelCompact,
} from './copilot-side-panel-context'

interface PanelHeaderProps {
  children: ReactNode
}

function PanelHeader({children}: PanelHeaderProps) {
  return <>{children}</>
}

interface PanelBodyProps {
  children: ReactNode
  className?: string
}

function PanelBody({children, className}: PanelBodyProps) {
  return <div className={clsx('flex-1 overflow-auto', className ?? 'tmp-p-3')}>{children}</div>
}

interface PanelFooterProps {
  children: ReactNode
}

function PanelFooter({children}: PanelFooterProps) {
  return <>{children}</>
}

interface PanelViewportFillProps {
  children: ReactNode
  fixedHeight?: boolean
  bottomInsetPx?: number
}

function PanelViewportFill({children, fixedHeight, bottomInsetPx}: PanelViewportFillProps) {
  const ref = useRef<HTMLDivElement>(null)
  useViewportFitHeight(ref, {
    recalculateOnScroll: !fixedHeight,
    bottomInsetPx,
  })
  return (
    <div ref={ref} className={styles.ViewportFill}>
      {children}
    </div>
  )
}

interface PanelDialogFillProps {
  children: ReactNode
}

function PanelDialogFill({children}: PanelDialogFillProps) {
  const ref = useRef<HTMLDivElement>(null)
  useDialogFillHeight(ref)
  return (
    <div ref={ref} className={styles.DialogFill}>
      {children}
    </div>
  )
}

interface PanelProps {
  heading: string
  children: ReactNode
  headerActions?: ReactNode
}

/**
 * Self-contained compound component for the copilot side panel chrome.
 * Renders the panel heading, a close button with managed focus refs,
 * and `Panel.Header`, `Panel.Body`, and `Panel.Footer` slot sub-components.
 */
function Panel({heading, children, headerActions}: PanelProps) {
  const {closeButtonRef, returnFocusRef} = useCopilotSidePanelFocusRefs()
  const {closePanel} = useCopilotSidePanelActions()
  const isCompact = useIsCopilotSidePanelCompact()
  const containerRef = useRef<HTMLDivElement>(null)
  const headingId = useId()
  const [isFullscreen, setIsFullscreen] = useState(
    () => ssrSafeWindow?.matchMedia('(max-width: calc(48rem - 0.02px))')?.matches ?? false,
  )

  const handleClose = useCallback(() => {
    const returnTarget = returnFocusRef.current
    closePanel()
    if (returnTarget) {
      requestAnimationFrame(() => returnTarget.focus())
    }
  }, [closePanel, returnFocusRef])

  const [slots] = useSlots(children, {
    header: PanelHeader,
    body: PanelBody,
    footer: PanelFooter,
  })

  useFocusTrap({containerRef, disabled: !isFullscreen})

  useOnEscapePress(() => {
    if (containerRef.current?.contains(document.activeElement)) {
      handleClose()
    }
  }, [handleClose])

  useEffect(() => {
    const mediaQuery = ssrSafeWindow?.matchMedia('(max-width: calc(48rem - 0.02px))')

    const handleChange = (event: MediaQueryListEvent) => {
      setIsFullscreen(event.matches)
      if (event.matches && !containerRef.current?.contains(document.activeElement)) {
        closeButtonRef.current?.focus()
      }
    }

    mediaQuery?.addEventListener('change', handleChange)
    return () => mediaQuery?.removeEventListener('change', handleChange)
  }, [closeButtonRef])

  return (
    <div
      ref={containerRef}
      role={isFullscreen ? 'dialog' : 'region'}
      aria-modal={isFullscreen ? true : undefined}
      aria-labelledby={headingId}
      className="d-flex flex-column height-full"
    >
      {isCompact && (
        <h3 id={headingId} className="sr-only">
          {heading}
        </h3>
      )}
      {(!isCompact || slots.header) && (
        <div className={styles.Header}>
          {!isCompact && (
            <div className={styles.HeaderContent}>
              <h3 id={headingId} className={styles.Heading}>
                {heading}
              </h3>
              <div className="d-flex flex-items-center">
                {headerActions}
                <IconButton
                  ref={closeButtonRef}
                  aria-label="Close side panel"
                  icon={XIcon}
                  variant="invisible"
                  size="medium"
                  onClick={handleClose}
                />
              </div>
            </div>
          )}
          {slots.header}
        </div>
      )}
      {slots.body}
      {slots.footer && <div className="tmp-p-3">{slots.footer}</div>}
    </div>
  )
}

Panel.Header = PanelHeader
Panel.Body = PanelBody
Panel.Footer = PanelFooter
Panel.ViewportFill = PanelViewportFill
Panel.DialogFill = PanelDialogFill

export {Panel}
